import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";
import { Link, matchRoutes, useLocation } from "react-router";
import { newIdempotencyKey } from "../../shared/api/client";
import { problemMessage } from "../../shared/api/problem";
import { ErrorSummary, FieldMessage } from "../../shared/forms/ErrorSummary";
import { useFormErrors } from "../../shared/forms/useFormErrors";
import type { FeedbackReportResult } from "../../shared/api/types";
import { useActiveProjectHint } from "../../shared/lib/activeProject";
import { useSession } from "../../shared/session/session-context";
import { screens } from "../../app/screens";
import { submitFeedbackReport } from "./api";
import { FEEDBACK_TITLE, useFeedbackOffer, type FeedbackOffer } from "./offer";
import { FEEDBACK_UI_ATTRIBUTE, captureScreenshot, describeElement, elementAt, failureMessage, type PinPoint, type ScreenshotResult } from "./capture";
import { diagnosticsSnapshot } from "./diagnostics";
import { getFeedbackPageState } from "./pageContext";
import { buildCapture, type FeedbackKindChoice, type PageFacts } from "./report";

const SUBMIT_TIMEOUT_MS = 60_000;
const POPOVER_WIDTH = 340;
/** The height assumed before the pop-up is measured (batch 25 GRF-83: then its real height, which grows with errors). */
const POPOVER_HEIGHT = 360;

const screenRoutes = screens.map((screen) => ({ path: screen.path, id: screen.path }));

/** The matched screen, its route params and the query string — what the engineer needs to open the same page. */
function usePageFacts(): () => PageFacts {
  const location = useLocation();
  return () => {
    const matches = matchRoutes(screenRoutes, location) ?? [];
    const match = matches[matches.length - 1];
    const screen = match ? screens.find((s) => s.path === match.route.path) : undefined;
    const routeParams: Record<string, string> = {};
    for (const [name, value] of Object.entries(match?.params ?? {})) {
      if (typeof value === "string") routeParams[name] = value;
    }
    return {
      url: window.location.href,
      route: screen?.path,
      routeParams,
      query: location.search,
      title: document.title,
      screenId: screen?.id,
      screenTitle: screen?.title,
    };
  };
}

/**
 * Header button: shown only while Features__PinFeedback is on, this environment has its feedback project AND the person is
 * not a partner (ruling 2026-10-01). A partner never asks for availability; the server also answers `available: false` and
 * refuses a report with 403, so hiding here is presentation, not the control.
 */
export function FeedbackButton({ projectKey }: { projectKey: string }) {
  const offer = useFeedbackOffer();
  const [open, setOpen] = useState(false);

  if (!offer) return null;
  return (
    <>
      <button
        type="button"
        className="btn sec gf-feedback-btn"
        data-guide="feedback"
        aria-pressed={open}
        title={FEEDBACK_TITLE}
        onClick={() => setOpen((value) => !value)}
      >
        Feedback
      </button>
      {open && <FeedbackLayer projectKey={projectKey} offer={offer} onClose={() => setOpen(false)} />}
    </>
  );
}

/** The pin layer, portalled to the body. */
export function FeedbackLayer({ projectKey, offer, onClose }: { projectKey: string; offer: FeedbackOffer; onClose: () => void }) {
  return createPortal(
    <PinFeedbackLayer
      projectKey={projectKey}
      feedbackProject={offer.projectKey}
      maxCommentLength={offer.maxCommentLength}
      maxScreenshotBytes={offer.maxScreenshotBytes}
      onClose={onClose}
    />,
    document.body,
  );
}

type Phase = "pin" | "compose" | "done";
type Busy = "idle" | "capturing" | "sending";

interface Pinned {
  point: PinPoint;
  element: ReturnType<typeof describeElement>;
  rect?: { left: number; top: number };
  screenshot: Promise<ScreenshotResult>;
}

interface Attempt {
  comment: string;
  kind: FeedbackKindChoice;
  key: string;
  reportJson: string;
  screenshot: Blob | null;
}

export interface PinFeedbackLayerProps {
  projectKey: string;
  feedbackProject: string;
  maxCommentLength: number;
  maxScreenshotBytes: number;
  onClose: () => void;
  /** Tests: replaces the screenshot capture. */
  capture?: (point: PinPoint, maxBytes: number) => Promise<ScreenshotResult>;
}

/**
 * Pin mode (crosshair over the whole page, Esc cancels) → click a spot → a popover anchored there: comment, Defect or
 * Change request, Submit. The screenshot starts at the click, before the popover exists, and never blocks the report: if
 * it fails, the report goes without it and says why.
 */
export function PinFeedbackLayer({ projectKey, feedbackProject, maxCommentLength, maxScreenshotBytes, onClose, capture = captureScreenshot }: PinFeedbackLayerProps) {
  const session = useSession();
  const pageFacts = usePageFacts();
  const hinted = useActiveProjectHint();
  const [phase, setPhase] = useState<Phase>("pin");
  const [pinned, setPinned] = useState<Pinned | null>(null);
  const [comment, setComment] = useState("");
  const [kind, setKind] = useState<FeedbackKindChoice | null>(null);
  const [busy, setBusy] = useState<Busy>("idle");
  // Shared form-error pattern: Submit is always offered; pressing it early names each missing answer, linked to it.
  const form = useFormErrors({
    values: { comment, kind },
    validate: (values) => [
      ...(values.comment.trim() ? [] : [{ field: "comment", message: "Say what you noticed." }]),
      ...(values.kind ? [] : [{ field: "kind", message: "Choose Defect or Change request." }]),
    ],
    order: ["comment", "kind"],
    idOf: (field) => `gf-pin-${field}`,
  });
  const [result, setResult] = useState<FeedbackReportResult | null>(null);
  const [screenshotNote, setScreenshotNote] = useState<string | null>(null);
  const attempt = useRef<Attempt | null>(null);
  // Batch 25 GRF-83: the pop-up is fixed to the window, so it is placed by its real height (the error summary and messages make it
  // taller than POPOVER_HEIGHT) and capped to the window, scrolling inside (app.css). Before, a pin low on the page left the
  // Submit button below the window with no way to scroll to it.
  const popRef = useRef<HTMLDivElement>(null);
  const [popHeight, setPopHeight] = useState(POPOVER_HEIGHT);
  const popOpen = phase !== "pin";
  useLayoutEffect(() => {
    const pop = popRef.current;
    if (!popOpen || !pop) return;
    const measure = () => setPopHeight(pop.offsetHeight || POPOVER_HEIGHT);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(pop);
    return () => observer.disconnect();
  }, [popOpen]);
  const flagsOn = useMemo(() => Object.keys(session.flags).filter((name) => session.flags[name]).sort(), [session.flags]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape" && busy === "idle") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [busy, onClose]);

  function onPin(event: ReactMouseEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    const point = { x: event.clientX, y: event.clientY };
    let element: Pinned["element"] = { selector: "body", tag: "body" };
    let rect: Pinned["rect"];
    try {
      const target = elementAt(point);
      element = describeElement(target);
      const box = target.getBoundingClientRect();
      rect = { left: box.left, top: box.top };
    } catch {
      // describing the element is best effort
    }
    const screenshot = capture(point, maxScreenshotBytes).catch(
      (failure: unknown): ScreenshotResult => ({ blob: null, info: { captured: false, error: failureMessage(failure) } }),
    );
    setPinned({ point, element, rect, screenshot });
    setPhase("compose");
  }

  async function onSubmit() {
    if (!pinned || busy !== "idle" || !form.attempt() || !kind) return;
    try {
      let current = attempt.current;
      if (!current || current.comment !== comment || current.kind !== kind) {
        setBusy("capturing");
        const shot = await pinned.screenshot;
        setScreenshotNote(shot.info.captured ? null : `No screenshot: ${shot.info.error ?? "not captured"}.`);
        const facts = pageFacts();
        const logs = diagnosticsSnapshot();
        const report = {
          kind,
          comment: comment.trim(),
          capture: buildCapture({
            page: facts,
            pin: pinned.point,
            elementRect: pinned.rect,
            element: pinned.element,
            viewing: {
              projectKey: facts.routeParams.projectKey ?? hinted ?? (projectKey || undefined),
              itemRef: facts.routeParams.ref,
              state: getFeedbackPageState(),
            },
            flagsOn,
            screenshot: shot.info,
            console: logs.console,
            apiCalls: logs.apiCalls,
          }),
        };
        current = { comment, kind, key: newIdempotencyKey(), reportJson: JSON.stringify(report), screenshot: shot.blob };
        attempt.current = current;
      }
      setBusy("sending");
      const filed = await withTimeout(submitFeedbackReport(current.reportJson, current.screenshot, current.key), SUBMIT_TIMEOUT_MS);
      setResult(filed);
      setPhase("done");
    } catch (failure) {
      form.fail(failure, { lead: problemMessage(failure), mapField: (field) => (field === "comment" || field === "kind" ? field : null) });
    } finally {
      setBusy("idle");
    }
  }

  const point = pinned?.point;
  const left = point ? Math.max(8, Math.min(point.x + 18, window.innerWidth - POPOVER_WIDTH - 8)) : 0;
  const top = point ? Math.max(8, Math.min(point.y + 18, window.innerHeight - Math.min(popHeight, window.innerHeight - 16) - 8)) : 0;

  return (
    <div {...{ [FEEDBACK_UI_ATTRIBUTE]: "" }} className="gf-pin-layer">
      {phase === "pin" && (
        <>
          <div className="gf-pin-overlay" data-testid="pin-overlay" onClick={onPin} />
          <div className="gf-pin-hint" role="status">
            Click the spot you want to comment on. Press Esc to cancel.
          </div>
        </>
      )}
      {point && <div className="gf-pin-marker" aria-hidden="true" style={{ left: point.x, top: point.y }} />}
      {phase !== "pin" && (
        <div ref={popRef} className="gf-pin-pop" role="dialog" aria-label="Feedback" style={{ left, top, width: POPOVER_WIDTH }}>
          {phase === "done" && result ? (
            <div className="stack">
              <p>
                Filed as{" "}
                {result.canView ? (
                  <Link to={`/items/${encodeURIComponent(result.key)}`} onClick={onClose}>
                    {result.key}
                  </Link>
                ) : (
                  <b>{result.key}</b>
                )}
                {result.workType === "CR" ? " (change request)" : " (defect)"}.
              </p>
              {!result.canView && <p className="help">The {feedbackProject} team will follow it up.</p>}
              {screenshotNote && <p className="help">{screenshotNote}</p>}
              {result.warnings.length > 0 && (
                <ul className="help">
                  {result.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              )}
              <div className="gf-pin-actions">
                <button type="button" className="btn" onClick={onClose}>
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                void onSubmit();
              }}
            >
              {/* The label sits above the summary, outside a .gf-field, so it turns red by class (D-R17-05). */}
              <label className={form.errorOf("comment") ? "gf-pin-label gf-invalid" : "gf-pin-label"} htmlFor="gf-pin-comment">
                What did you notice?
              </label>
              <ErrorSummary {...form.summary} />
              <textarea
                {...form.controlProps("comment")}
                rows={4}
                maxLength={maxCommentLength}
                value={comment}
                autoFocus
                onChange={(event) => setComment(event.target.value)}
              />
              <FieldMessage id="gf-pin-comment" message={form.errorOf("comment")} />
              <fieldset
                className="gf-pin-kind"
                id="gf-pin-kind"
                tabIndex={-1}
                aria-invalid={form.errorOf("kind") ? true : undefined}
                aria-describedby={form.errorOf("kind") ? form.errorIdFor("kind") : undefined}
              >
                <legend>This is a</legend>
                <label>
                  <input type="radio" name="gf-pin-kind" value="defect" checked={kind === "defect"} onChange={() => setKind("defect")} /> Defect
                </label>
                <label>
                  <input type="radio" name="gf-pin-kind" value="change_request" checked={kind === "change_request"} onChange={() => setKind("change_request")} /> Change
                  request
                </label>
                <FieldMessage id="gf-pin-kind" message={form.errorOf("kind")} />
              </fieldset>
              <p className="help">
                Sends a screenshot with the pin, this page and what was on it, recent errors and API calls, and the server log to {feedbackProject}.
                Passwords and tokens are removed.
              </p>
              {busy !== "idle" && (
                <p className="help" role="status">
                  {busy === "capturing" ? "Capturing the screen…" : "Sending…"}
                </p>
              )}
              <div className="gf-pin-actions">
                <button type="submit" className="btn" disabled={busy !== "idle"}>
                  Submit
                </button>
                <button type="button" className="btn sec" onClick={onClose} disabled={busy !== "idle"}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Giraffe did not answer in time. Try again: the report is filed only once.")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (failure: unknown) => {
        clearTimeout(timer);
        reject(failure instanceof Error ? failure : new Error(String(failure)));
      },
    );
  });
}
